import { customFilterTypes } from "./constants/filterConstants";
import { isEqual, keyBy, orderBy, pullAt } from "lodash-es";
import { sortIfDaysOfWeek } from "./constants/constants";
import { findQueryFieldWithAliasInQuery } from "./queries";
import { LabeledValueState, MenuFilterState } from "../types/menuFilters";
import { produce } from "immer";

export function sortMenuFilters(values) {
  return sortByDate(values);
}

function sortByDate(values) {
  if (!values.length) {
    return values;
  }

  const type = customFilterTypes.find((d) => d.type === values[0].type);

  if (type) {
    const elements = values.map((x) => ({
      ...x,
      sortValue: type.fn(x.value),
    }));

    const sorted = orderBy(elements, "sortValue", type.direction);

    return sorted.map((x) => {
      delete x.sortValue;
      return x;
    });
  }

  return sortIfDaysOfWeek(values);
}

/**
 * @param {Query} query
 * @param currentFields {FieldNameWithAlias[]}
 */
export function assembleEmptySingleQueryFiltersFromActiveTableStore(
  query,
  currentFields
) {
  const ret: SingleQueryFilter[] = [];

  for (const fieldNameWithAlias of currentFields) {
    const fieldName = fieldNameWithAlias.fieldName;
    if (fieldName === "sketchimage") {
      // we will want to add proper support for automatically hiding
      // api-image type active table columns rather than do this hack.
      continue;
    }
    const fieldWithAlias = findQueryFieldWithAliasInQuery(
      query,
      fieldNameWithAlias
    );
    if (!fieldWithAlias) {
      continue;
    }
    const fieldInfo = fieldWithAlias.field;

    const type = fieldInfo?.mapping.type;
    const values = type === "boolean" ? [true, false, null] : null;

    const row: SingleQueryFilter = {
      fieldName,
      alias: fieldWithAlias.alias,
      fieldDisplayName: fieldInfo?.mapping.displayName,
      values,
      totalCount: null,
      areValuesFixed: values ? true : undefined,
    };

    if (type) {
      row.fieldType = fieldInfo.mapping.type;
    }

    ret.push(row);
  }

  return ret;
}

type SingleQueryFilter = {
  fieldName: string;
  alias: string | null;
  fieldDisplayName: string | null | undefined;
  values: null | Array<boolean | null>;
  totalCount: number | null;
  areValuesFixed: true | undefined;
  fieldType?: string;
};

/**
 * Updates existing menu filters in the state based on updated menu filters
 */
export function mergeMenuFilters(
  currentMenuFiltersState: MenuFilterState[],
  newMenuFiltersState: MenuFilterState[]
): MenuFilterState[] {
  const newMenuFiltersByUuid = keyBy(newMenuFiltersState, "uuid");
  return produce(currentMenuFiltersState, (draft) => {
    for (const [_i, menuFilterDraft] of Object.entries(draft)) {
      const i = Number(_i);
      const menuFilter = currentMenuFiltersState[i];
      const newMenuFilter = newMenuFiltersByUuid[menuFilter.uuid];
      if (!newMenuFilter) {
        continue;
      }
      const checkedValues = menuFilter.values.filter((v) => v.checked);

      const newValues = newMenuFilter.values.map((v) => ({
        ...v,
        checked: false,
      }));

      if (!checkedValues.length) {
        menuFilterDraft.values = newValues;
        continue;
      }

      const existingIndexesToDrop: number[] = [];
      const newIndexesWithOldEquivalents: number[] = [];

      for (const [_valueIndex, valueDraft] of Object.entries(
        menuFilterDraft.values
      )) {
        const valueIndex = Number(_valueIndex);
        const value = menuFilter.values[valueIndex];
        const matchingValueIndex = findMatchingValueIndex(
          newMenuFilter.values,
          value
        );

        if (valueDraft.checked) {
          // Keep checked ones.

          newIndexesWithOldEquivalents.push(matchingValueIndex);
          continue;
        }

        if (matchingValueIndex < 0) {
          // The value is neither checked, nor is among the new values. So mark it for removal.
          existingIndexesToDrop.push(valueIndex);
        }

        // This new value was found among the existing value, so add it to the list of new values
        // that shouldn't be added to the value list.
        newIndexesWithOldEquivalents.push(matchingValueIndex);
      }

      pullAt(menuFilterDraft.values, existingIndexesToDrop);
      const newValuesToAdd = [...newMenuFilter.values];
      pullAt(newValuesToAdd, newIndexesWithOldEquivalents);

      for (const value of newValuesToAdd) {
        menuFilterDraft.values.push({ ...value, checked: false });
      }
    }

    function findMatchingValueIndex(
      values: LabeledValueState[],
      valueToFind: LabeledValueState
    ) {
      return values.findIndex((v) => {
        if (v.value === valueToFind.value) {
          return true;
        }

        return typeof v.value === "object"
          ? // Only do deep comparison check if the value is an object.
            isEqual(v.value, valueToFind.value)
          : false;
      });
    }
  });
}
